import React, { useMemo, useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Search, MapPin, Briefcase, Users, Star, ChevronRight, ChevronLeft, Clock, TrendingUp, Building2, ArrowRight, Filter, Globe, Award, Target, Gem, X } from 'lucide-react';
import { CrownIcon } from '../components/ui/icons';
import { Job } from '@/pages/providers/AppProvider';
import { normalizeTier } from '../utils/tier';
import { ImageWithFallback } from '../components/ui/ImageWithFallback';
import { normalizeMediaUrl, getAvatarUrl as resolveAvatarUrl } from '../utils/media';
import Loader from '../components/ui/Loader';
import { JobCardUpdated as JobCard } from '../components/JobCardUpdated';
import { useData } from '../context/DataContext';
import { useApp } from "@/pages/providers/AppProvider";
import { Container, Section, Grid, Input } from '../components/ui/shared';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '../components/ui/select';
import { SearchIcon, BriefcaseIcon, TrendingUpIcon, UsersIcon, BuildingIcon } from '../components/ui/icons';
import { HeroCarousel } from '../components/HeroCarousel';
import { NewsAnnouncement } from '../components/NewsAnnouncementEnhanced';

import { CompanySection } from '../components/CompanySection';
import { SEO } from '../components/SEO';
import { apiClient } from '@/lib/api-client';

const AVATAR_PLACEHOLDER = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjOTk5IiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0iI2YwZjBmMCIgc3Ryb2tlLXdpZHRoPSIzIj48Y2lyY2xlIGN4PSIzMiIgY3k9IjI0IiByPSIxMiIvPjxwYXRoIGQ9Ik0xNiA1MmgzMiIgLz48cGF0aCBkPSJNMjIgNDQgczIgNiAxMCA2IDEwLTYgMi02IiAvPjwvc3ZnPg=='

interface HomePageProps {
  onNavigate: (page: string) => void;
  onViewJob: (jobId: string) => void;
  onViewCompany?: (companyName: string) => void;
  filters: {
    location: string;
    type: string;
    category: string;
    salary: string;
    experience: string;
  };
  onFilterChange: (filters: any) => void;
  onSaveJob?: (jobId: string) => void;
  savedJobs?: string[];
  isUserLoggedIn?: boolean;
  onLoginRequired?: () => void;
}

export const HomePage = React.memo(function HomePage({
  onNavigate,
  onViewJob,
  onViewCompany,
  filters,
  onFilterChange,
  onSaveJob,
  savedJobs = [],
  isUserLoggedIn = false,
  onLoginRequired
}: HomePageProps) {
  const { jobs, loading, error, adminCities, adminIndustries } = useData();
  const { handleApplyJob, siteSettings, applications, currentUser, companies = [] } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('');
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [locationFilterText, setLocationFilterText] = useState('');
  const [categoryFilterText, setCategoryFilterText] = useState('');





  // Get tier colors
  const getTierColor = (tier: string) => {
    const t = normalizeTier(tier) || 'latest';
    const cfg = siteSettings?.tierConfig?.[t];
    return cfg?.badgeClass || (
      t === 'megajob' ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white' :
        t === 'premium' ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white' :
          t === 'prime' ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white' :
            t === 'newspaper' ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' :
              'bg-[#1d73bb] text-white'
    );
  };

  const getTierBgColor = (tier: string) => {
    const t = normalizeTier(tier) || 'latest';
    const cfg = siteSettings?.tierConfig?.[t];
    return cfg?.bgClass || (
      t === 'megajob' ? 'bg-yellow-50' :
        t === 'premium' ? 'bg-blue-50' :
          t === 'prime' ? 'bg-green-50' :
            t === 'newspaper' ? 'bg-orange-50' :
              'bg-gray-50'
    );
  };

  // Filter jobs based on search criteria
  const filteredJobs = useMemo(() => {
    const isExpired = (j: any) => {
      const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
      if (!raw) return false;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return false;
      return d.getTime() <= Date.now();
    };
    return jobs.filter(job => {
      const matchesSearch = !searchQuery ||
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.company.toLowerCase().includes(searchQuery.toLowerCase()) ||
        job.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesLocation = !location ||
        job.location.toLowerCase().includes(location.toLowerCase());

      const matchesCategory = !category ||
        job.category.toLowerCase().includes(category.toLowerCase());

      return !isExpired(job) && matchesSearch && matchesLocation && matchesCategory;
    });
  }, [jobs, searchQuery, location, category, applications, currentUser]);

  const availableLocations = useMemo(() => {
    const cityOnly = (val: any): string => {
      const raw = String(val || '').trim();
      if (!raw) return '';
      if (raw.includes(' - ')) {
        const after = raw.split(' - ')[1] || raw;
        const city = after.split(',')[0].trim();
        return city;
      }
      const toks = raw.split(/[,-]/).map(s => s.trim()).filter(Boolean);
      const skipWords = ['nepal', 'province', 'bagmati', 'gandaki', 'koshi', 'lumbini', 'madhesh', 'sudurpashchim'];
      for (const t of toks) {
        const k = t.toLowerCase();
        if (!skipWords.some(w => k.includes(w))) return t;
      }
      return toks[0] || raw;
    };
    const src = Array.isArray(adminCities) && adminCities.length ? adminCities : [];
    if (src.length) {
      const set = new Set<string>();
      src.forEach((name: string) => { const v = cityOnly(name); if (v) set.add(String(v)); });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    const set = new Set<string>();
    jobs.forEach(j => { const v = cityOnly(j.location); if (v) set.add(v); });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [adminCities, jobs]);

  const availableCategories = useMemo(() => {
    const src = Array.isArray(adminIndustries) && adminIndustries.length ? adminIndustries : [];
    if (src.length) {
      const set = new Set<string>();
      src.forEach((name: string) => { if (name) set.add(String(name)); });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    }
    const set = new Set<string>();
    jobs.forEach(j => j.category && set.add(j.category));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [adminIndustries, jobs]);

  // Select a single highlighted job per company and compute total count
  const companyJobsMap = useMemo(() => {
    const map = new Map<string, Job[]>();
    filteredJobs.forEach(job => {
      const companyName = (job as any)?.company?.name || (job as any)?.company || '';
      const key = String(companyName).trim().toLowerCase();
      if (!key) return;

      const isApplied = currentUser?.type === 'jobseeker' && Array.isArray(applications) && applications.some(a => String(a.jobId) === String(job.id));
      const jobWithApplied = { ...job, isApplied } as any;

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(jobWithApplied);
    });
    return map;
  }, [filteredJobs, currentUser, applications]);

  // Select a single highlighted job per company and compute total count
  const dedupedJobsByCompany = useMemo(() => {
    // Tier priority: megajob > premium > prime > newspaper > latest
    const tierRank = (t: string | undefined) => {
      const n = normalizeTier(t);
      switch (n) {
        case 'megajob': return 5;
        case 'premium': return 4;
        case 'prime': return 3;
        case 'newspaper': return 2;
        default: return 1; // latest/unknown
      }
    };

    const picked: Job[] = [];

    companyJobsMap.forEach((allJobs, key) => {
      if (allJobs.length === 0) return;

      // Find the best job to represent the company
      let bestJob = allJobs[0];
      for (let i = 1; i < allJobs.length; i++) {
        const a = bestJob;
        const b = allJobs[i];
        const rankA = tierRank(a.tier);
        const rankB = tierRank(b.tier);
        const dateA = new Date(a.publishedDate || a.postedDate || 0).getTime();
        const dateB = new Date(b.publishedDate || b.postedDate || 0).getTime();

        if (rankB > rankA || (rankB === rankA && dateB > dateA)) {
          bestJob = b;
        }
      }

      picked.push({
        ...bestJob,
        companyJobCount: allJobs.length,
        allCompanyJobs: allJobs
      });
    });

    // Sort by tier rank then date for consistent ordering
    return picked.sort((a, b) => {
      const ra = tierRank((a as any).tier);
      const rb = tierRank((b as any).tier);
      if (rb !== ra) return rb - ra;
      const ad = new Date((a as any).publishedDate || (a as any).postedDate || 0).getTime();
      const bd = new Date((b as any).publishedDate || (b as any).postedDate || 0).getTime();
      return bd - ad;
    });
  }, [companyJobsMap]);

  // Categorize filtered jobs by tier
  const jobsByTier = useMemo(() => {
    const categorized = {
      megajob: [] as Job[],
      premium: [] as Job[],
      prime: [] as Job[],
      newspaper: [] as Job[],
      latest: [] as Job[]
    };
    // Use deduped one-per-company list for Home page
    dedupedJobsByCompany.forEach(job => {
      const tier = normalizeTier(job.tier);
      if (tier === 'megajob') {
        categorized.megajob.push(job);
      } else if (tier === 'premium') {
        categorized.premium.push(job);
      } else if (tier === 'prime') {
        categorized.prime.push(job);
      } else if (tier === 'newspaper') {
        categorized.newspaper.push(job);
      } else {
        categorized.latest.push(job);
      }
    });
    return categorized;
  }, [dedupedJobsByCompany]);

  const [scrollCategories, setScrollCategories] = useState<any[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiClient.getScrollCategories();
        const items = Array.isArray(res?.categories) ? res.categories : Array.isArray(res) ? res : [];
        if (!cancelled) setScrollCategories(items);
      } catch { }
    })();
    return () => { cancelled = true; };
  }, []);

  // Calculate real job counts for the scrolling categories
  const categoriesWithCounts = useMemo(() => {
    if (!scrollCategories.length) return [];

    // 1. Build company industry lookup
    const companyIndustryMap: Record<string, string> = {};
    if (Array.isArray(companies)) {
      companies.forEach(c => {
        const id = String((c as any)._id || c.id || '').trim();
        const ind = (c as any).industry || (c as any).category || '';
        if (id && ind) companyIndustryMap[id] = String(ind).toLowerCase().trim();
      });
    }

    // 2. Count jobs by category name and ID
    const counts: Record<string, number> = {};

    // Helper to normalize strings for comparison
    const norm = (s: any) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

    // Help determine if a job is active/expired
    const isExpired = (j: any) => {
      const raw = j?.expiryDate || j?.expiry_date || j?.deadline;
      if (!raw) return false;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return false;
      return d.getTime() <= Date.now();
    };

    jobs.forEach(j => {
      // ONLY count active, non-expired jobs
      if (isExpired(j)) return;

      const jobCatRaw = String(j.category || '').trim();
      const jobCatNorm = norm(jobCatRaw);
      const companyIndNorm = norm(companyIndustryMap[String((j as any).companyId || (j as any).company_id || '').trim()] || '');

      // We want to associate this job with ONE primary category name and its ID
      const categoryNamesToIncrement = new Set<string>();
      const categoryIdsToIncrement = new Set<string>();

      // A. If we have a raw category (could be name or ID)
      if (jobCatRaw) {
        categoryIdsToIncrement.add(jobCatRaw);
        if (jobCatNorm) categoryNamesToIncrement.add(jobCatNorm);
      }

      // B. If job category is generic/missing/ID, fallback to company industry
      const isGeneric = !jobCatNorm || ['general', 'other', 'unknown', 'none'].includes(jobCatNorm);
      const isId = /^[0-9a-fA-F]{24}$/.test(jobCatRaw) || /^\d+$/.test(jobCatRaw);

      if (isGeneric || isId) {
        if (companyIndNorm) categoryNamesToIncrement.add(companyIndNorm);
      }

      // C. Apply synonyms (if one is added, add the other)
      const syn = (name: string) => {
        if (name === 'healthcare' || name === 'health care') return ['healthcare', 'health care'];
        if (['it', 'technology', 'information technology'].includes(name)) return ['it', 'technology', 'information technology'];
        return [name];
      };

      const finalNames = new Set<string>();
      categoryNamesToIncrement.forEach(n => syn(n).forEach(s => finalNames.add(s)));

      // D. Increment counts
      finalNames.forEach(n => { counts[n] = (counts[n] || 0) + 1; });
      categoryIdsToIncrement.forEach(id => { counts[id] = (counts[id] || 0) + 1; });
    });

    return scrollCategories.map(c => {
      const nameKey = norm(c.name);
      const idKey = String(c.id || c._id || '').trim();

      // Sum counts by checking Name and ID
      const countByName = counts[nameKey] || 0;
      const countById = idKey ? (counts[idKey] || 0) : 0;

      return {
        ...c,
        count: Math.max(countByName, countById)
      };
    });
  }, [scrollCategories, jobs, companies]);

  const [trustedCompanies, setTrustedCompanies] = useState<any[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.getTrustedCompanies(20);
        const list = Array.isArray((res as any)?.companies) ? (res as any).companies : (Array.isArray(res) ? (res as any) : []);
        const mapped = list.map((c: any, idx: number) => ({
          id: c._id || c.id || String(idx),
          name: c.name,
          color: c.brand_color || c.color || '#1F2937',
          logo: c.logo_url || null,
          isActive: c.is_active !== false
        }));
        if (!cancelled) setTrustedCompanies(mapped);
      } catch {
        if (!cancelled) setTrustedCompanies([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Filter active companies for display
  const activeCompanies = useMemo(() => trustedCompanies.filter((company: any) => company.isActive), [trustedCompanies]);

  const [testimonials, setTestimonials] = useState<any[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiClient.getTestimonials();
        const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);
        const mapped = list.map((t: any) => ({
          name: t.name,
          role: t.role,
          company: t.company,
          image: normalizeMediaUrl(t.avatar_url) || AVATAR_PLACEHOLDER,
          content: t.text
        }));
        if (!cancelled) setTestimonials(mapped);
      } catch {
        if (!cancelled) setTestimonials([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => testimonials.length ? (prev + 1) % testimonials.length : 0);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => testimonials.length ? (prev - 1 + testimonials.length) % testimonials.length : 0);
  };





  const handleSearch = () => {
    onFilterChange({ ...filters, search: searchQuery, location, category });
    onNavigate('jobs');
  };

  const clearSearch = () => {
    setSearchQuery('');
    setLocation('');
    setCategory('');
  };

  const hasActiveFilters = searchQuery || location || category;

  if (loading) {
    return <Loader fullScreen text="Loading Mega Jobs..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="MegaJobNepal - #1 Job Portal in Nepal"
        description="Find your dream job in Nepal with MegaJobNepal. Browse thousands of vacancies in Banking, IT, NGOs, and more. Post jobs for free."
      />
      {/* Hero Carousel Section */}
      <HeroCarousel>
        <div className="responsive-container text-center">
          <h1 className="hero-title text-gray-900 mb-4 sm:mb-6">
            Find Your Dream Job In Nepal With <span className="text-blue-600">Mega Jobs</span>
          </h1>
          <p className="hero-subtitle max-w-3xl mx-auto mb-4 sm:mb-6 px-4">
            Search and Apply for the Best Jobs in Nepal Today
          </p>

          {/* Search Bar */}
          <div className="max-w-6xl mx-auto bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-4 sm:p-6 mb-4 sm:mb-8">
            <div className="mobile-search">
              <div className="flex-1 relative mb-3 sm:mb-0">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
                <Input
                  placeholder="Search jobs in Nepal..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 sm:pl-10 mobile-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="w-full sm:w-52">
                  <Select value={location || 'all'} onValueChange={(v) => setLocation(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder="Any Location" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="all">Any Location</SelectItem>
                      <div className="px-2 pb-2">
                        <Input
                          placeholder="Search location..."
                          value={locationFilterText}
                          onChange={(e) => setLocationFilterText(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <SelectSeparator />
                      {availableLocations
                        .filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase()))
                        .sort((a, b) => a.localeCompare(b))
                        .map((loc, index) => (
                          <SelectItem key={`${loc}-${index}`} value={loc} textValue={loc}>
                            {loc}
                          </SelectItem>
                        ))}
                      {availableLocations.filter((l) => l.toLowerCase().includes(locationFilterText.toLowerCase())).length === 0 && (
                        <SelectGroup>
                          <SelectLabel>No matches</SelectLabel>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-52">
                  <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue placeholder="Any Category" />
                    </SelectTrigger>
                    <SelectContent position="popper">
                      <SelectItem value="all">Any Category</SelectItem>
                      <div className="px-2 pb-2">
                        <Input
                          placeholder="Search category..."
                          value={categoryFilterText}
                          onChange={(e) => setCategoryFilterText(e.target.value)}
                          className="h-8"
                        />
                      </div>
                      <SelectSeparator />
                      {availableCategories
                        .filter((n) => n.toLowerCase().includes(categoryFilterText.toLowerCase()))
                        .sort((a, b) => a.localeCompare(b))
                        .map((cat, index) => (
                          <SelectItem key={`${cat}-${index}`} value={cat} textValue={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      {availableCategories.filter((n) => n.toLowerCase().includes(categoryFilterText.toLowerCase())).length === 0 && (
                        <SelectGroup>
                          <SelectLabel>No matches</SelectLabel>
                        </SelectGroup>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 sm:gap-4">
                  <Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 btn-responsive touch-button">
                    <Search className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Search</span>
                  </Button>
                  {hasActiveFilters && (
                    <Button onClick={clearSearch} variant="outline" className="border-gray-300 btn-responsive touch-button">
                      <X className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Clear</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4">
            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 btn-responsive touch-button"
              onClick={() => onNavigate('auth')}
            >
              <Briefcase className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Post a Job
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="btn-responsive touch-button"
              onClick={() => onNavigate('auth')}
            >
              <Users className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
              Register/Login
            </Button>
          </div>
        </div>
      </HeroCarousel>







      {/* Job Tiers Section with News Sidebar */}
      <section className="pt-4 sm:pt-6 lg:pt-8 pb-8 sm:pb-12 lg:pb-16 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="w-full pl-4 sm:pl-6 lg:pl-8 lg:pr-0">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="responsive-heading text-[#1d73bb] mb-3 sm:mb-4">Job Opportunities by Tier</h2>
            {(searchQuery || location || category) && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200 mx-4">
                <p className="text-blue-800 font-medium text-sm sm:text-base">
                  Showing {filteredJobs.length} jobs
                  {searchQuery && ` matching "${searchQuery}"`}
                  {location && ` in ${location}`}
                  {category && ` in ${category} category`}
                </p>
              </div>
            )}
          </div>

          {/* Main Content with Sidebar Layout */}
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Main Content Area */}
            <div className="flex-1 w-full space-y-6 sm:space-y-8">

              {/* MegaJob Section (always visible) */}
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 px-2 sm:px-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-14 h-14 sm:w-18 sm:h-18 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <Target className="w-12 h-12 sm:w-14 sm:h-14 text-[#FF6600]" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-3xl font-bold text-[#1d73bb]">Mega Jobs</h3>
                      <p className="text-gray-600 text-sm sm:text-lg">Premium positions from top companies</p>
                    </div>
                  </div>
                  <Button onClick={() => onNavigate('jobs')} className="btn-responsive touch-button text-xs sm:text-sm bg-[#1d73bb] hover:bg-[#175f98] active:bg-[#134e7d] text-white">
                    View All Mega Jobs <ChevronRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>

                <div className="job-grid-full-width">
                  {jobsByTier.megajob.length === 0 ? (
                    <div className="col-span-full py-8 text-center text-gray-600 border rounded-lg bg-yellow-50/50">
                      No MegaJob listings available at the moment.
                    </div>
                  ) : (
                    jobsByTier.megajob.slice(0, 10).map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        onViewJob={onViewJob}
                        onViewCompany={onViewCompany}
                        tierColor="bg-gradient-to-r from-yellow-500 to-amber-500 text-white"
                        tierBgColor="border-2 border-yellow-200 bg-gradient-to-br from-yellow-50 to-white"
                        onSaveJob={onSaveJob}
                        onApply={handleApplyJob}
                        onLoginRequired={onLoginRequired}
                        isSaved={savedJobs.includes(job.id)}
                        isApplied={job.isApplied as boolean}
                        isUserLoggedIn={isUserLoggedIn}
                        imageMode="job"
                        showTierBadge={false}
                        allCompanyJobs={job.allCompanyJobs}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Premium Jobs (always visible) */}
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 px-2 sm:px-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-14 h-14 sm:w-18 sm:h-18 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <Gem className="w-12 h-12 sm:w-14 sm:h-14 text-[#FF6600]" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-3xl font-bold text-[#1d73bb]">Premium Jobs</h3>
                      <p className="text-gray-600 text-sm sm:text-lg">Enhanced visibility and special highlighting</p>
                    </div>
                  </div>
                  <Button onClick={() => onNavigate('jobs')} className="btn-responsive touch-button text-xs sm:text-sm bg-[#1d73bb] hover:bg-[#175f98] active:bg-[#134e7d] text-white">
                    View All Premium Jobs <ChevronRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>

                <div className="job-grid-full-width">
                  {jobsByTier.premium.length === 0 ? (
                    <div className="col-span-full py-8 text-center text-gray-600 border rounded-lg bg-blue-50/50">
                      No Premium listings available at the moment.
                    </div>
                  ) : (
                    jobsByTier.premium.slice(0, 10).map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        onViewJob={onViewJob}
                        onViewCompany={onViewCompany}
                        tierColor="bg-gradient-to-r from-blue-500 to-indigo-500 text-white"
                        tierBgColor="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white"
                        onSaveJob={onSaveJob}
                        onApply={handleApplyJob}
                        onLoginRequired={onLoginRequired}
                        isSaved={savedJobs.includes(job.id)}
                        isApplied={job.isApplied as boolean}
                        isUserLoggedIn={isUserLoggedIn}
                        imageMode="job"
                        showTierBadge={false}
                        allCompanyJobs={job.allCompanyJobs}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Prime Jobs (always visible) */}
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 px-2 sm:px-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-14 h-14 sm:w-18 sm:h-18 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <CrownIcon size="xxl" className="sm:h-14 sm:w-14 text-[#FF6600]" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-3xl font-bold text-[#1d73bb]">Prime Jobs</h3>
                      <p className="text-gray-600 text-sm sm:text-lg">Featured placement with priority listing</p>
                    </div>
                  </div>
                  <Button onClick={() => onNavigate('jobs')} className="btn-responsive touch-button text-xs sm:text-sm bg-[#1d73bb] hover:bg-[#175f98] active:bg-[#134e7d] text-white">
                    View All Prime Jobs <ChevronRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>

                <div className="job-grid-full-width">
                  {jobsByTier.prime.length === 0 ? (
                    <div className="col-span-full py-8 text-center text-gray-600 border rounded-lg bg-green-50/50">
                      No Prime listings available at the moment.
                    </div>
                  ) : (
                    jobsByTier.prime.slice(0, 10).map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        onViewJob={onViewJob}
                        onViewCompany={onViewCompany}
                        tierColor="bg-gradient-to-r from-green-500 to-emerald-500 text-white"
                        tierBgColor="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white"
                        onSaveJob={onSaveJob}
                        onApply={handleApplyJob}
                        onLoginRequired={onLoginRequired}
                        isSaved={savedJobs.includes(job.id)}
                        isApplied={job.isApplied as boolean}
                        isUserLoggedIn={isUserLoggedIn}
                        imageMode="job"
                        showTierBadge={false}
                        allCompanyJobs={job.allCompanyJobs}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Newspaper Jobs (always visible) */}
              <div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 px-2 sm:px-4">
                  <div className="flex items-center space-x-1 sm:space-x-2">
                    <div className="w-14 h-14 sm:w-18 sm:h-18 rounded-xl sm:rounded-2xl flex items-center justify-center">
                      <Briefcase className="w-12 h-12 sm:w-14 sm:h-14 text-[#FF6600]" />
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-3xl font-bold text-[#1d73bb]">Newspaper Jobs</h3>
                      <p className="text-gray-600 text-sm sm:text-lg">Basic listing for newspaper jobs</p>
                    </div>
                  </div>
                  <Button onClick={() => onNavigate('jobs')} className="btn-responsive touch-button text-xs sm:text-sm bg-[#1d73bb] hover:bg-[#175f98] active:bg-[#134e7d] text-white">
                    View All Newspaper Jobs <ChevronRight className="ml-2 w-3 h-3 sm:w-4 sm:h-4" />
                  </Button>
                </div>

                <div className="job-grid-full-width">
                  {jobsByTier.newspaper.length === 0 ? (
                    <div className="col-span-full py-8 text-center text-gray-600 border rounded-lg bg-orange-50/50">
                      No Newspaper listings available at the moment.
                    </div>
                  ) : (
                    jobsByTier.newspaper.slice(0, 10).map((job) => (
                      <JobCard
                        key={job.id}
                        job={job}
                        onViewJob={onViewJob}
                        onViewCompany={onViewCompany}
                        tierColor="bg-gradient-to-r from-orange-500 to-red-500 text-white"
                        tierBgColor="border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white"
                        onSaveJob={onSaveJob}
                        onApply={handleApplyJob}
                        onLoginRequired={onLoginRequired}
                        isSaved={savedJobs.includes(job.id)}
                        isApplied={job.isApplied as boolean}
                        isUserLoggedIn={isUserLoggedIn}
                        imageMode="job"
                        showTierBadge={false}
                        allCompanyJobs={job.allCompanyJobs}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Removed global View All Jobs CTA below Newspaper section as requested */}
            </div>

            {/* News & Announcement Sidebar */}
            <div className="hidden lg:block w-64 flex-shrink-0 sticky top-6">
              <NewsAnnouncement />
            </div>

            {/* Mobile News Section */}
            <div className="lg:hidden mt-8 w-full">
              <NewsAnnouncement />
            </div>
          </div>
        </div>
      </section>





      {/* Latest Jobs */}
      <Section background="white" padding="md">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-4 px-2 sm:px-4">
            <div>
              <h2 className="responsive-heading text-[#1d73bb] mb-2 sm:mb-4">Latest Job Opportunities</h2>
              <p className="responsive-subheading">Fresh opportunities posted today</p>
            </div>
            <Button
              onClick={() => onNavigate('jobs')}
              variant="outline"
              className="btn-responsive touch-button flex items-center gap-2"
            >
              View All Jobs <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="job-grid-full-width">
            {filteredJobs.length === 0 ? (
              <div className="col-span-full py-8 text-center text-gray-600 border rounded-lg bg-gray-50/50">
                No new job opportunities available at the moment.
              </div>
            ) : (
              [...filteredJobs]
                .sort((a, b) => {
                  const aDate = new Date(a.publishedDate || a.postedDate || 0).getTime();
                  const bDate = new Date(b.publishedDate || b.postedDate || 0).getTime();
                  return bDate - aDate;
                })
                .slice(0, 20) // Show more jobs in the latest section
                .map((job) => {
                  const companyName = (job as any)?.company?.name || (job as any)?.company || '';
                  const key = String(companyName).trim().toLowerCase();
                  const companyJobs = companyJobsMap.get(key) || [];

                  return (
                    <JobCard
                      key={job.id}
                      job={{ ...job, companyJobCount: companyJobs.length }}
                      onViewJob={onViewJob}
                      onViewCompany={onViewCompany}
                      tierColor={getTierColor(job.tier)}
                      tierBgColor={getTierBgColor(job.tier)}
                      onSaveJob={onSaveJob}
                      onApply={handleApplyJob}
                      onLoginRequired={onLoginRequired}
                      isSaved={savedJobs.includes(job.id)}
                      isApplied={currentUser?.type === 'jobseeker' && Array.isArray(applications) && applications.some(a => String(a.jobId) === String(job.id))}
                      isUserLoggedIn={isUserLoggedIn}
                      imageMode="job"
                      allCompanyJobs={companyJobs}
                    />
                  );
                })
            )}
          </div>
        </div>
      </Section>

      {/* Trusted Companies Section - Scrolling Animation */}
      <Section background="white" padding="sm">
        <div className="max-w-full mx-auto overflow-x-hidden">
          <div className="text-center mb-4 sm:mb-8 px-4">
            <h2 className="text-lg sm:text-xl md:text-2xl font-medium text-[#1d73bb] mb-2">
              Trusted By <span className="text-orange-500">1000+</span> Companies
            </h2>
            <p className="text-sm sm:text-base text-gray-600">Leading organizations across industries choose MegaJobNepal</p>
          </div>

          {/* Scrolling Companies Container */}
          <div className="relative overflow-x-hidden">
            <div className="flex animate-scroll-right-to-left space-x-6 w-max">
              {[...activeCompanies, ...activeCompanies].map((company, index) => (
                <div
                  key={`${company.id}-${index}`}
                  className="flex items-center bg-white/95 rounded-xl px-4 py-3 min-w-[240px] whitespace-nowrap shadow-sm hover:shadow-lg transition-all"
                >
                  <div
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg bg-white overflow-hidden mr-3 flex-shrink-0"
                  >
                    <ImageWithFallback
                      src={normalizeMediaUrl(company.logo) || 'https://placehold.co/80x80?text=Logo'}
                      alt={company.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <span
                    className="text-base sm:text-lg font-semibold tracking-wide"
                    style={{ color: company.color }}
                  >
                    {company.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Job Categories Section - Scrolling + Grid */}
      <Section background="gray" padding="md">
        <Container>
          <div className="text-center mb-4 sm:mb-6 px-4">
            <h2 className="responsive-heading text-[#1d73bb] mb-2 sm:mb-4">Browse Jobs by Category</h2>
            <p className="responsive-subheading">Find opportunities in your field of expertise</p>
          </div>

          {/* Scrolling marquee when categories are marked for scroll */}
          {categoriesWithCounts.length > 0 && (
            <div className="relative overflow-x-hidden px-4 mb-6">
              <div className="flex animate-scroll-right-to-left space-x-4 w-max">
                {(categoriesWithCounts.length >= 6 ? [...categoriesWithCounts, ...categoriesWithCounts] : categoriesWithCounts).map((category: any, index) => (
                  <div key={`cat-scroll-${index}`} className="flex items-center bg-white rounded-xl px-4 py-2 border shadow-sm hover:shadow-md transition-shadow min-w-[220px]">
                    <div className="w-9 h-9 rounded-md border bg-white overflow-hidden mr-3 flex-shrink-0">
                      <ImageWithFallback
                        src={normalizeMediaUrl(category.icon_url) || 'https://placehold.co/80x80?text=Icon'}
                        alt={category.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm sm:text-base font-semibold text-[#1d73bb]">{category.name}</span>
                      <span className="text-xs text-gray-500">{Number(category.count || 0)} jobs</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Removed duplicate grid below scrolling to avoid repeated display */}
        </Container>
      </Section>

      {/* Testimonials Section */}
      <Section background="gray" padding="md">
        <Container>
          <div className="text-center mb-6 sm:mb-10 px-4">
            <h2 className="responsive-heading text-[#1d73bb] mb-2 sm:mb-4">What Our Users Say</h2>
            <p className="responsive-subheading">Hear from job seekers and employers who found success with MegaJob</p>
          </div>

          <div className="relative max-w-4xl mx-auto px-4">
            <Card className="shadow-lg testimonial-card">
              <CardContent className="content-padding">
                <div className="text-center">
                  <ImageWithFallback
                    src={(testimonials[currentTestimonial] || {}).image || AVATAR_PLACEHOLDER}
                    alt={(testimonials[currentTestimonial] || {}).name || 'User'}
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full mx-auto mb-3 sm:mb-4 object-cover"
                    fallbackSrc={resolveAvatarUrl(((testimonials[currentTestimonial] || {}).name || ''), 64)}
                  />
                  <blockquote className="text-sm sm:text-lg text-gray-600 mb-3 sm:mb-4 italic">
                    "{(testimonials[currentTestimonial] || {}).content || 'Success Story...'}"
                  </blockquote>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm sm:text-base">{(testimonials[currentTestimonial] || {}).name || ''}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">
                      {((testimonials[currentTestimonial] || {}).role || '')} {((testimonials[currentTestimonial] || {}).company ? 'at ' + (testimonials[currentTestimonial] || {}).company : '')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation buttons */}
            <button
              type="button"
              aria-label="Previous"
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none outline-none cursor-pointer no-hover-transform"
              onClick={prevTestimonial}
            >
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
            <button
              type="button"
              aria-label="Next"
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none outline-none cursor-pointer no-hover-transform"
              onClick={nextTestimonial}
            >
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </Container>
      </Section>


    </div>
  );
});
